import { Text } from '@oneaudi/unified-web-components';
import { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { ReactCompareSlider, ReactCompareSliderHandle } from 'react-compare-slider';
import { VisualItem } from '~/app/compositions/content/gallery/items/gallery-items';
import { GalleryItemDefaultStyle } from '~/app/compositions/content/gallery/styles/gallery-style';
import { useI18n } from '~/app/i18n/i18n';
import { ConfiguredCarCatalogImage } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';

interface CompareGalleryProps extends PropsWithChildren {
	currentMedia: Array<Array<ConfiguredCarCatalogImage>>;
	enableComparison?: boolean;
	aspectRatio?: string;
	index?: number;
	trackComparisonInteraction: () => void;
}

export const GalleryCompare = ({
	enableComparison,
	aspectRatio = '4 / 3',
	index = 0,
	currentMedia,
	children,
	trackComparisonInteraction,
}: CompareGalleryProps) => {
	const { i18n } = useI18n();
	const { carIdentifier } = useConfigurator();
	const toCompareToMedia = useRef<Array<ConfiguredCarCatalogImage[]>>([]);
	const prevMedia = useRef<Array<ConfiguredCarCatalogImage[]>>(currentMedia);
	const carId = JSON.stringify(carIdentifier);
	const prevId = useRef<string>(carId);

	const positionRef = useRef<number>(50); // 50 is the default position;
	const hasInteracted = useRef<boolean>(false);

	useEffect(() => {
		if (carId !== prevId?.current) {
			toCompareToMedia.current = prevMedia.current;
			prevMedia.current = currentMedia;
			prevId.current = carId;
		}
	}, [carId]);

	const rootStyles = useMemo(
		() =>
			enableComparison
				? {
						visibility: 'visible' as const,
						position: 'static' as const,
					}
				: {
						visibility: 'hidden' as const,
						position: 'absolute' as const,
					},
		[enableComparison],
	);

	return (
		<ReactCompareSlider
			onPointerUp={() => {
				// to track the first interaction with the comparison slider
				if (positionRef.current !== 50 && !hasInteracted.current) {
					hasInteracted.current = true;
					trackComparisonInteraction();
				}
			}}
			onPositionChange={(position) => {
				positionRef.current = position;
			}}
			disabled={!enableComparison}
			style={{ height: '100%' }}
			handle={
				<ReactCompareSliderHandle
					linesStyle={{ background: 'rgba(255, 255, 255, 0.25)', boxShadow: 'none', width: '1px' }}
					buttonStyle={{
						border: 'none',
						backgroundColor: 'rgba(128, 128, 128, 0.5)',
						backdropFilter: 'none',
						boxShadow: 'none',
					}}
					style={rootStyles}
				/>
			}
			itemOne={children}
			itemTwo={
				enableComparison ? (
					toCompareToMedia.current && toCompareToMedia.current.length > 0 ? (
						<GalleryItemDefaultStyle $aspectRatio={aspectRatio}>
							<VisualItem item={toCompareToMedia?.current?.[index]?.[0]} size={'large'} />
						</GalleryItemDefaultStyle>
					) : (
						<div className="comparison-information">
							<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
								{i18n('ngc.common.comparison.emtpy')}
							</Text>
						</div>
					)
				) : null
			}
		/>
	);
};
